/**
 * VATHub — landing for the "الضريبة المضافة" section.
 *
 * 2 cards:
 *   • Tax Control (settings — rate, enable toggle, TRN)
 *   • Tax Report  (empty placeholder for now)
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Percent, FileBarChart2, ChevronRight } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { usePerms } from '../../permissions/PermissionsContext';

export default function VATHub() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const cards = [
    {
      id: 'control',
      titleAr: 'التحكم بالضريبة',
      titleEn: 'Tax Control',
      descAr: 'إعداد نسبة الضريبة، تفعيل/إيقاف تطبيقها، ومزامنة الرقم الضريبي من أوراق الشركة',
      descEn: 'Configure the tax rate, toggle its application, and sync the TRN from company documents',
      icon: Percent,
      color: '#d4af37',
      route: '/dashboard/finance/vat/control',
    },
    {
      id: 'report',
      titleAr: 'التقرير الضريبي',
      titleEn: 'Tax Report',
      descAr: 'تقارير وإقرارات الضريبة (قريباً)',
      descEn: 'Tax reports and declarations (coming soon)',
      icon: FileBarChart2,
      color: '#94a3b8',
      route: '/dashboard/finance/vat/report',
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#EF4444]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'الضريبة المضافة', label_en: 'VAT', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('الضريبة المضافة', 'VAT')}
          </h1>
          <p className="text-white/50 text-sm">
            {t('اختر ما تريد الوصول إليه', 'Pick a section')}
          </p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visiblePages(cards, 'route').map((c) => (
            <motion.button
              key={c.id}
              whileHover={{ y: -6 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(c.route)}
              className="group relative bg-white/[0.02] rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/40 text-start"
              data-testid={`vat-hub-card-${c.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: `${c.color}15`, border: `1px solid ${c.color}30` }}>
                  <c.icon className="w-7 h-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{isAr ? c.titleAr : c.titleEn}</h3>
                  <p className="text-white/60 text-xs leading-relaxed">{isAr ? c.descAr : c.descEn}</p>
                </div>
                <ChevronRight className={`w-5 h-5 text-white/40 group-hover:text-[#d4af37] ${isAr ? 'rotate-180' : ''}`} />
              </div>
            </motion.button>
          ))}
        </motion.div>
      </main>
    </div>
  );
}
